import I18n from 'I18n';
import { useMemo, useState } from 'react';
import { AddIcon } from 'foundations-assets';
import PlaylistAssignmentProgress from './PlaylistAssignmentProgress';
import { useFilteredPlaylistAssignmentsForPortal } from '../playlistAssignmentsHooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import styled from 'academy-common-lib/utils/Styling';
import { TABLE_PAGINATOR_SIZE, AcademyTable } from '../../table';
import { useFullPlaylist } from '../../savedItems/savedItemsQueries';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import formatName from 'I18n/utils/formatName';
import CompletedIcon from '../../../components/ContentCards/ReducedContentCard/subcomponents/CompletedIcon';
import { AssignPlaylistButton } from './AssignPlaylistButton';
import { UnAssignPlaylistButton } from './UnAssignPlaylistButton';
import { Text } from 'foundations-components/components/Text';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PlaylistItemHeader = styled.th.withConfig({
  displayName: "PlaylistAssignmentsForPortalTable__PlaylistItemHeader",
  componentId: "sc-1pey585-0"
})(["min-width:200px;"]);
const PlaylistAssignmentsForPortalTableHeaderActions = ({
  selectedRows,
  deselectAllRows,
  playlistId,
  portalId
}) => {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "500",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "detail-200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `learningCenter.playlistAssignments.assignmentsSelected`,
        options: {
          count: selectedRows.length
        }
      })
    }), /*#__PURE__*/_jsx(UnAssignPlaylistButton, {
      assignments: selectedRows.map(id => Number(id)),
      onDeleted: deselectAllRows
    }), /*#__PURE__*/_jsx(AssignPlaylistButton, {
      assignmentIds: selectedRows.map(id => Number(id)),
      marginInlineStart: "0 !important",
      playlistId: playlistId,
      portalId: portalId,
      use: "link",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(IconHolder, {
          icon: AddIcon,
          padding: "0"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.playlistAssignments.addDueDates"
        })]
      })
    })]
  });
};
const PlaylistAssignmentsForPortalTableHeader = ({
  playlistId
}) => {
  const {
    displayedPlaylistItems
  } = useFullPlaylist(playlistId);
  if (!displayedPlaylistItems) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("th", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlistAssignments.columns.user"
      })
    }), /*#__PURE__*/_jsx("th", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlistAssignments.columns.dueDate"
      })
    }), displayedPlaylistItems.map(({
      itemId,
      name
    }) => /*#__PURE__*/_jsx(PlaylistItemHeader, {
      children: /*#__PURE__*/_jsx(TruncateString, {
        matchContentWidth: true,
        children: name
      })
    }, itemId))]
  });
};
const PlaylistAssignmentsForPortalTableRow = assignment => {
  const {
    assignee: {
      firstName,
      lastName,
      userId
    },
    dueDate,
    playlistId,
    portalId,
    progress,
    id
  } = assignment;
  const displayName = formatName({
    firstName,
    lastName
  });
  const {
    displayedPlaylistItems
  } = useFullPlaylist(playlistId);
  const {
    itemsProgress
  } = progress || {
    itemsProgress: []
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(PlaylistAssignmentProgress, {
        assignment: assignment
      })
    }), /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx(AssignPlaylistButton, {
        assignmentIds: [id],
        playlistId: playlistId,
        portalId: portalId,
        use: "link",
        children: dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
          format: "L",
          type: "utc",
          value: dueDate
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.common.add"
        })
      })
    }), displayedPlaylistItems && displayedPlaylistItems.map(({
      itemId,
      name,
      type
    }) => {
      const itemProgress = itemsProgress.find(ip => ip.itemId === itemId && ip.type === type);
      return /*#__PURE__*/_jsx("td", {
        title: I18n.text('learningCenter.playlistAssignments.assignmentProgressLabel', {
          itemName: name,
          assigneeName: displayName
        }),
        children: itemProgress && itemProgress.status !== 'NOT_STARTED' ? itemProgress.status === 'COMPLETED' ? /*#__PURE__*/_jsx(CompletedIcon, {}) : /*#__PURE__*/_jsx(FormattedPercentage, {
          value: itemProgress.progressLevel
        }) : null
      }, `${portalId}_${userId}_${itemId}`);
    })]
  });
};
const PlaylistAssignmentsForPortalTable = ({
  playlistId,
  portalId
}) => {
  const [page, setPage] = useState(1);
  const {
    playlistAssignmentsForPortal,
    pageCount
  } = useFilteredPlaylistAssignmentsForPortal({
    playlistId,
    portalId,
    page
  });
  const rows = useMemo(() => {
    const assignmentsRecord = new Map();
    if (playlistAssignmentsForPortal) playlistAssignmentsForPortal.forEach(assignment => {
      assignmentsRecord.set(assignment.id.toString(), assignment);
    });
    return assignmentsRecord;
  }, [playlistAssignmentsForPortal]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(AcademyTable, {
      renderHeader: () => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableHeader, {
        playlistId: playlistId
      }),
      renderHeaderActions: ({
        selectedRows,
        deselectAllRows
      }) => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableHeaderActions, {
        deselectAllRows: deselectAllRows,
        playlistId: playlistId,
        portalId: portalId,
        selectedRows: selectedRows
      }),
      renderRow: data => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableRow, Object.assign({}, data)),
      rows: rows
    }), pageCount > 1 && /*#__PURE__*/_jsx(Paginator, {
      maxVisiblePageButtons: TABLE_PAGINATOR_SIZE,
      onPageChange: ({
        target: {
          value
        }
      }) => setPage(value),
      page: page,
      pageCount: pageCount
    })]
  });
};
export default PlaylistAssignmentsForPortalTable;